import { DateTime } from 'luxon';
import type { GroupMonthlyReport } from '../generated-api/models';
import { spendingByMonth, type MonthSpendingRow } from './report-data';

export const BUCKET_TREND_CHART_MONTHS = 6;
const AVERAGE_MONTHS = 6;

export interface BucketTrend {
  /** The history month and the five before it, oldest first. */
  chartRows: MonthSpendingRow[];
  current: MonthSpendingRow | null;
  previousMonth: MonthSpendingRow | null;
  /** Average of the six full months before the history month that the budget existed. */
  average: number | null;
  /** The same month a year before the viewed month, so planning a future month can look back at it. */
  sameMonthLastYear: MonthSpendingRow | null;
}

/**
 * History never runs past today: a future month has no spending yet, and its budgets
 * would count it as a month the budget existed, so empty bars would drag the average down.
 */
export function bucketTrendHistoryMonth(monthYear: string, currentMonthYear: string): string {
  return monthYear > currentMonthYear ? currentMonthYear : monthYear;
}

/** Thirteen months ending with the history month: enough for a year-ago comparison. */
export function bucketTrendRange(monthYear: string, currentMonthYear: string) {
  const end = bucketTrendHistoryMonth(monthYear, currentMonthYear);
  return {
    fromMonthYear: DateTime.fromISO(end).minus({ months: 12 }).toFormat('yyyy-MM'),
    toMonthYear: end,
  };
}

export function bucketTrend(
  report: GroupMonthlyReport,
  bucketId: string,
  monthYear: string,
  currentMonthYear: string,
): BucketTrend {
  const historyMonth = bucketTrendHistoryMonth(monthYear, currentMonthYear);
  const rows = spendingByMonth(report, { kind: 'bucket', id: bucketId });
  const index = rows.findIndex(row => row.monthYear === historyMonth);
  const upToViewed = index === -1 ? rows : rows.slice(0, index + 1);
  const existed = (row: MonthSpendingRow) => row.budgeted !== null || row.spent !== 0;
  const averageRows = upToViewed.slice(0, -1).slice(-AVERAGE_MONTHS).filter(existed);
  const yearAgo = DateTime.fromISO(monthYear).minus({ years: 1 }).toFormat('yyyy-MM');
  const sameMonthLastYear = rows.find(row => row.monthYear === yearAgo) ?? null;
  const previousMonth = upToViewed.at(-2) ?? null;

  return {
    chartRows: upToViewed.slice(-BUCKET_TREND_CHART_MONTHS),
    current: upToViewed.at(-1) ?? null,
    previousMonth: previousMonth && existed(previousMonth) ? previousMonth : null,
    average: averageRows.length
      ? Math.round((averageRows.reduce((sum, row) => sum + row.spent, 0) / averageRows.length) * 100) / 100
      : null,
    sameMonthLastYear: sameMonthLastYear && existed(sameMonthLastYear) ? sameMonthLastYear : null,
  };
}
